import _     from 'lodash';
import Model from './model';
import { BelongsTo, HasMany, HasManyThrough } from './associations';

export default class AssociationProxy {
  constructor(entity, associationInjector) {
    this.entity                = entity;
    this.associationInjector   = associationInjector;
    this.associations          = associationInjector.associations
    this.entitySpace           = associationInjector.entitySpace
    this.collectionConstructor = associationInjector.collectionConstructor

    this.associations.forEach(association => {
      if (!(this.entity instanceof association.sourceModel)) { return; }

      Object.defineProperty(this, association.name, { get: this._getterFor(association) });
    });
  }

  _getterFor(association) {
    switch (association.constructor) {
      case BelongsTo:      return this._belongsToGetter(association);
      case HasMany:        return this._hasManyGetter(association);
      case HasManyThrough: return this._hasManyThroughGetter(association);
    }
  }

  _belongsToGetter(association) {
    return function() { // this must return a function
      const map = this.entitySpace.mapForOrNull(association.destinationModel);

      if (!map) { throw this._associationNotLoaded(association); }

      const pk = this.entity[association.foreignKey];

      if (_.isNil(pk)) { return null; }

      const entity = map.get(pk);

      if (!entity) { throw this._destinationModelNotLoaded(association, pk); }

      return this.associationInjector.inject(entity);
    };
  }

  _hasManyGetter(association) {
    return function() { // this must return a function
      const map = this.entitySpace.mapForOrNull(association.destinationModel);

      if (!map) { throw this._associationNotLoaded(association); }

      const collection = this.collectionConstructor.with(
        map, association.destinationModel, this.entitySpace, this.associationInjector
      );

      return collection.where(record => record[association.foreignKey] === this.entity.pk);
    };
  }

  _hasManyThroughGetter(association) {
    return function() { // this must return a function
      let model      = association.sourceModel;
      let collection = this.collectionConstructor.with(
        [this.entity], model, this.entitySpace, this.associationInjector
      );

      for (let name of association.assocNames) {
        const assoc = this.associations.findBy(model, name);
        model       = assoc.destinationModel;
        collection  = this._assocUnionFor(collection, model, name);
      }

      return collection;
    };
  }

  _assocUnionFor(collection, model, assocName) {
    if (collection.size()) {
      const collections = collection.map(
        entity => this._collectionFor(entity.associations[assocName], model)
      );

      return this.collectionConstructor.union(collections);
    } else {
      return this._collectionFor(null, model);
    }
  }

  _collectionFor(entities, model) {
    if (entities instanceof this.collectionConstructor) {
      return entities;
    } else if (entities instanceof Model) {
      return this.collectionConstructor.with([entities], model, this.entitySpace, this.associationInjector)
    } else if (_.isNil(entities)) {
      return this.collectionConstructor.with([],         model, this.entitySpace, this.associationInjector)
    } else {
      throw new Error('bad entities param for AssociationProxy._collectionFor:', entities)
    }
  }

  _associationNotLoaded(association) {
    return new Error(`association ${association.sourceModel.name}.${association.name} not loaded`);
  }

  _destinationModelNotLoaded(association, pk) {
    return new Error(
      `${association.destinationModel.name} with pk ${pk} for association ` +
      `${association.sourceModel.name}.${association.name} not loaded`
    );
  }
}
